import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { fmtDay, formatPrice } from '../../shared/data/common';
import { reviewInsurance } from '../../api/insurance';
import { coverDays, destinationLabel, journeyFromType, journeyLabel, parseInsuranceQuery, toInsuranceParams } from './searchModel';
import { benefitValue, groupBenefits, insurerName, planTitle, providerName, regionTitle } from './planFormat';
import { exclusions } from './data';

export default function InsuranceDetails() {
  const q = useQuery();
  const { state } = useLocation();
  const navigate = useNavigate();
  const search = parseInsuranceQuery(q);
  const resultsPath = `/insurance/results?${new URLSearchParams(toInsuranceParams(search)).toString()}`;
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const { supplier, planId, productId } = q;

  useEffect(() => {
    if (!supplier || !planId || !productId) return undefined;
    const controller = new AbortController();
    setError('');
    reviewInsurance({ supplierCode: supplier, planId, productId }, controller.signal)
      .then(setReview)
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message || 'We could not confirm the price of this plan.'); });
    return () => controller.abort();
  }, [supplier, planId, productId, attempt]);

  if (!supplier || !planId || !productId) {
    return (
      <div className="container section empty">
        <h2>Plan not found</h2>
        <Link to="/insurance" className="btn btn-primary">Search insurance</Link>
      </div>
    );
  }

  // Until the review answers, show the plan from the results page (if we came from there).
  const plan = review?.plan || state?.plan;
  const ages = review?.travellerAges || search.ages;
  const start = review?.startDate || search.start;
  const end = review?.endDate;
  const buy = () => navigate('/insurance/review', {
    state: { review, destinations: search.dest, backTo: `/insurance/plan?${new URLSearchParams(q).toString()}` },
  });

  return (
    <>
      <FlowBand title={plan ? `${planTitle(plan)} plan` : 'Plan details'} step={1} back={{ to: resultsPath, label: 'Back to plans' }} />
      <div className="container details-layout">
        <div className="details-main">
          {plan ? (
            <section className="panel ins-plan-head">
              <span className="tag">{insurerName(plan.insurer)}</span>
              <h2 className="details-title">{planTitle(plan)}</h2>
              <span className="muted">Medical cover up to <b>{plan.coverAmount}</b>{plan.regionName && <> · Valid in {regionTitle(plan.regionName)}</>}</span>
              {plan.assistanceProviders.length > 0 && (
                <div className="amenities">{plan.assistanceProviders.map((p) => <span key={p} className="tag">{providerName(p)}</span>)}</div>
              )}
            </section>
          ) : !error && (
            <section className="panel" role="status"><span className="spinner sm" /> Loading plan…</section>
          )}

          {plan && groupBenefits(plan).map(([group, items]) => (
            <section key={group} className="panel">
              <h3>{group}</h3>
              <table className="data-table ins-benefit-table">
                <thead><tr><th>Benefit</th><th>Cover</th></tr></thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.name}>
                      <td><b>{b.name}</b>{b.description && <span className="muted small block">{b.description}</span>}</td>
                      <td><b>{benefitValue(b)}</b>{b.provider && <span className="muted small block">by {providerName(b.provider)}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}

          <section className="panel">
            <h3>Key exclusions</h3>
            <ul className="policy-list cross-list">{exclusions.map((e) => <li key={e}>{e}</li>)}</ul>
            <p className="muted small">Read the policy wording emailed with your policy for the full terms.</p>
          </section>

          <section className="panel">
            <h3>Cancellation</h3>
            <ul className="policy-list">
              <li>Cancel online up to 24 hours before cover starts for a refund of the premium.</li>
              <li>Once cover has started, the policy cannot be cancelled.</li>
              <li>The convenience fee is not refunded.</li>
            </ul>
          </section>
        </div>

        <aside className="details-side">
          <div className="panel price-card">
            {error ? (
              <div className="api-message error" role="alert">
                <p>{error}</p>
                <div className="btn-row">
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt(attempt + 1)}>Try again</button>
                  <Link to={resultsPath} className="btn btn-primary btn-sm">Search again</Link>
                </div>
              </div>
            ) : (
              <>
                <span className="muted small">{review ? 'Total price' : 'Confirming price…'}</span>
                <b className="price big" aria-busy={!review}>{plan ? formatPrice(plan.price.total) : '—'}</b>
                {plan && (
                  <div className="fare-breakdown">
                    <div className="summary-line"><span>Premium</span><span>{formatPrice(plan.price.premium)}</span></div>
                    {plan.price.convenienceFee > 0 && (
                      <div className="summary-line">
                        <span>Convenience fee{review?.convenienceFeeNote && <small className="muted fee-note">{review.convenienceFeeNote}</small>}</span>
                        <span>{formatPrice(plan.price.convenienceFee)}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="kv-grid one">
                  <div><span className="muted small">Cover type</span><b>{journeyLabel(review ? journeyFromType(review.journeyType) : search.journey)}</b></div>
                  <div><span className="muted small">Travelling to</span><b>{destinationLabel(search.dest)}</b></div>
                  <div><span className="muted small">Cover dates</span><b>{fmtDay(start)}{end && <> – {fmtDay(end)}</>}</b>{end && <span className="muted small">{coverDays(start, end)} days</span>}</div>
                  <div>
                    <span className="muted small">Travellers</span>
                    <b>{ages.length} · age{ages.length === 1 ? '' : 's'} {ages.join(', ')}</b>
                    {plan?.travellerPrices?.length > 0 && (
                      <span className="muted small">{plan.travellerPrices.map((t) => `Age ${t.age}: ${formatPrice(t.premium)}`).join(' · ')}</span>
                    )}
                  </div>
                </div>
                <button type="button" className="btn btn-primary block lg" onClick={buy} disabled={!review}>
                  <Icon name="insurance" size={18} /> {review ? 'Buy this plan' : 'Confirming price…'}
                </button>
              </>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
